import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import "./AgreementsRegistry.css";
import { UserContext } from "../../../contexts/CurrentUserContext";
import { getAllCounterparties, getUsersDirectory } from "../../../api/api";
import { getAllBlocks, getAllProjectsSummary } from "../../../api/projectApi";
import { getAllSections } from "../../../api/projectEstimatesApi";
import {
  exportAgreementXlsx,
  getAgreementApprovals,
  getAgreements,
  getCurrentAgreementApproval,
} from "../../../api/agreementsApi";
import {
  APPROVAL_STATUS_LABELS,
  APPROVAL_STEP_STATUS_LABELS,
  AGREEMENT_LIST_STATUS_LABELS,
} from "../ProjectFinance/Agreements/agreementsListHelpers";
import { getAgreementSectionModuleTitles } from "../ProjectFinance/Agreements/agreementFormHelpers";
import {
  formatAgreementNumber,
  formatCurrency,
  formatDateRu,
  formatUserDisplayName,
  getCounterpartyDisplayName,
  PAYMENT_STATUS_LABELS,
  PERFORMER_TYPE_LABELS,
  WORKFLOW_STATUS_LABELS,
} from "../ProjectFinance/AgreementsV2/agreementsHelpers";
import {
  filterCounterpartySelectOption,
  getCounterpartyLabel,
  getCounterpartySelectLabel,
} from "../ProjectFinance/AgreementsV2/addSubcontractorHelpers";
import { selectStyles } from "../../../utils/selectStyles";

export const AGREEMENTS_REGISTRY_ROLES = [
  "АДМС",
  "ФИН",
  "АДМП",
  "РО",
  "НО",
  "ГИП",
  "ИК",
];

const FINANCE_LINK_ROLES = ["АДМС", "АДМП", "ГИП", "РО", "ФИН"];

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const FETCH_PAGE_SIZE = 100;

const APPROVAL_STATUS_CODES = new Set([
  "pending",
  "on_approval",
  "on approval",
  "на согласовании",
]);

const isApprovalListStatus = (status) =>
  status === "on_approval" || status === "pending";

const projectKindOfName = (name) => {
  const tokens = String(name || "")
    .toUpperCase()
    .split(/[^0-9A-ZА-ЯЁ]+/)
    .filter(Boolean);
  if (tokens.includes("МЖК")) return "МЖК";
  if (tokens.includes("ОЗ")) return "ОЗ";
  return "";
};

const STATUS_FILTER_OPTIONS = [
  { value: "draft", label: "Черновик" },
  { value: "on_approval", label: "На согласовании" },
  { value: "approved", label: "Согласовано" },
  { value: "rejected", label: "Отклонено" },
  { value: "active", label: "Активно" },
  { value: "closed", label: "Закрыто" },
  { value: "paid_advance", label: "Оплачен аванс" },
  { value: "overdue_advance", label: "Просрочен аванс" },
  { value: "expected_ODGE", label: "Ожидается ОДГЭ" },
  { value: "expected_OPGE", label: "Ожидается ОПГЭ" },
  { value: "overdue_OPGE", label: "Просрочен ОПГЭ" },
];

const AGREEMENT_STATUS_LABELS = {
  ...PAYMENT_STATUS_LABELS,
  ...AGREEMENT_LIST_STATUS_LABELS,
  signed: "Подписано",
  on_approval: "На согласовании",
};

const matchesApprovalListStatus = (agreement) => {
  const values = [agreement?.status, agreement?.approval_summary?.status];
  if (
    values.some((value) =>
      APPROVAL_STATUS_CODES.has(String(value || "").trim().toLowerCase()),
    )
  ) {
    return true;
  }
  const label = AGREEMENT_STATUS_LABELS[agreement?.status] ?? agreement?.status ?? "";
  return label === "На согласовании";
};

const levelName = (level) => (typeof level === "string" ? level : level?.name);

export const userHasAgreementsRegistryAccess = (userData) =>
  Boolean(
    userData?.access_levels?.some((level) =>
      AGREEMENTS_REGISTRY_ROLES.includes(levelName(level)),
    ),
  );

const userHasFinanceLink = (userData) =>
  Boolean(
    userData?.access_levels?.some((level) =>
      FINANCE_LINK_ROLES.includes(levelName(level)),
    ),
  );

const asArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const normalizeName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const badgeKind = (status) => {
  const value = String(status || "");
  if (!value) return "idle";
  if (value === "rejected" || value.includes("overdue")) return "bad";
  if (
    value === "approved" ||
    value === "signed" ||
    value === "completed" ||
    value.includes("paid")
  ) {
    return "paid";
  }
  if (
    value === "draft" ||
    value === "closed" ||
    value === "active" ||
    value === "not_started"
  ) {
    return "idle";
  }
  return "wait";
};

const StatusBadge = ({ status, labels }) => (
  <span className={`agr-badge agr-badge--${badgeKind(status)}`}>
    {labels?.[status] ?? status ?? "—"}
  </span>
);

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const stepRole = (step) =>
  step?.role_type ||
  step?.role_name ||
  step?.role ||
  step?.approver_role ||
  step?.position ||
  "";

const stepLabel = (step) =>
  step?.approver_name ||
  (step?.approver_id != null ? `ID ${step.approver_id}` : "—");

const sortSteps = (steps) =>
  [...(Array.isArray(steps) ? steps : [])].sort(
    (a, b) => (a?.step_order ?? 0) - (b?.step_order ?? 0),
  );

const getSectionRows = (agreement, sectionNameById) => {
  const priceById = new Map();
  (Array.isArray(agreement?.section_prices) ? agreement.section_prices : []).forEach(
    (item) => {
      const id = Number(item?.section_id);
      if (Number.isFinite(id)) priceById.set(id, item?.price);
    },
  );
  const ids = [
    ...new Set([
      ...(Array.isArray(agreement?.section_ids) ? agreement.section_ids : []).map(
        Number,
      ),
      ...priceById.keys(),
    ]),
  ].filter((id) => Number.isFinite(id));

  return ids.map((id) => {
    const baseName = sectionNameById[id] || `Раздел #${id}`;
    const moduleTitles = priceById.has(id)
      ? getAgreementSectionModuleTitles(agreement, id)
      : [];
    return {
      id,
      name: moduleTitles.length
        ? `${baseName} · ${moduleTitles.join(", ")}`
        : baseName,
      price: priceById.has(id) ? priceById.get(id) : null,
    };
  });
};

const resolveBlockName = (block, blockNameById) => {
  if (block?.block_name) return block.block_name;
  const blockId = Number(block?.block_id);
  if (Number.isFinite(blockId) && blockNameById?.[blockId]) {
    return blockNameById[blockId];
  }
  return block?.block_id != null ? `Блок #${block.block_id}` : "Блок";
};

const getBlockRows = (agreement, blockNameById = {}) => {
  const details = Array.isArray(agreement?.agreement_blocks_detail)
    ? agreement.agreement_blocks_detail
    : [];
  if (details.length > 0) {
    return details.map((block) => ({
      id: block.id ?? block.block_id,
      name: resolveBlockName(block, blockNameById),
      price: block.price,
      complete: Boolean(block.complete),
    }));
  }
  const blocks = Array.isArray(agreement?.agreement_blocks)
    ? agreement.agreement_blocks
    : [];
  return blocks.map((block) => ({
    id: block.id ?? block.block_id,
    name: resolveBlockName(block, blockNameById),
    price: block.price,
    complete: Boolean(block.complete),
  }));
};

const resolveProjectName = (agreement, projectNameById) => {
  if (agreement?.project_name) return agreement.project_name;
  const projectId = Number(agreement?.project_id);
  if (Number.isFinite(projectId) && projectNameById?.[projectId]) {
    return projectNameById[projectId];
  }
  if (agreement?.project_id != null) return `Проект #${agreement.project_id}`;
  return "—";
};

const resolveProjectKind = (agreement, projectNameById, projectKindById) => {
  const projectId = Number(agreement?.project_id);
  const fromCatalog =
    Number.isFinite(projectId) ? projectKindById?.[projectId] : "";
  if (fromCatalog === "МЖК" || fromCatalog === "ОЗ") return fromCatalog;
  return projectKindOfName(resolveProjectName(agreement, projectNameById));
};

const agreementNames = (agreement) =>
  [
    agreement?.counterparty_summary?.display_name,
    agreement?.counterparty_summary?.full_name,
    agreement?.counterparty_summary?.full_company_name,
    agreement?.counterparty_summary?.short_company_name,
    agreement?.company_name,
    agreement?.counterparty_name,
    agreement?.subcontractor_send_summary?.counterparty_name,
    ...(Array.isArray(agreement?.performer_names) ? agreement.performer_names : []),
  ]
    .map(normalizeName)
    .filter(Boolean);

const matchesPerformer = (agreement, performer) => {
  if (!performer) return true;
  const id = Number(performer.id);
  const selectedName = normalizeName(performer.name);

  if (performer.source === "user") {
    const ids = (Array.isArray(agreement?.performer_ids) ? agreement.performer_ids : [])
      .map(Number)
      .filter((value) => Number.isFinite(value));
    if (ids.includes(id)) return true;
    return selectedName
      ? (Array.isArray(agreement?.performer_names) ? agreement.performer_names : []).some(
          (name) => normalizeName(name) === selectedName,
        )
      : false;
  }

  if (
    Number(agreement?.counterparty_id) === id ||
    Number(agreement?.counterparty_summary?.id) === id ||
    Number(agreement?.company_id) === id ||
    Number(agreement?.subcontractor_send_summary?.counterparty_id) === id
  ) {
    return true;
  }

  return selectedName ? agreementNames(agreement).includes(selectedName) : false;
};

const SORT_ACCESSORS = {
  number: (item) => Number(item?.number) || 0,
  price: (item) => Number(item?.price) || 0,
  date_from: (item) => item?.date_from || "",
  status: (item) => item?.status || "",
};

const sortAgreements = (list, ordering) => {
  if (!ordering) return list;
  const desc = ordering.startsWith("-");
  const field = desc ? ordering.slice(1) : ordering;
  const read = SORT_ACCESSORS[field];
  if (!read) return list;
  return [...list].sort((a, b) => {
    const left = read(a);
    const right = read(b);
    if (left < right) return desc ? 1 : -1;
    if (left > right) return desc ? -1 : 1;
    return 0;
  });
};

const fetchAllAgreements = async (params, isCancelled) => {
  const first = await getAgreements({
    ...params,
    page: 1,
    page_size: FETCH_PAGE_SIZE,
  });
  if (isCancelled()) return [];
  const firstResults = asArray(first?.results ?? first);
  const total = Number(first?.count ?? firstResults.length) || 0;
  const collected = [...firstResults];
  const pages = Math.ceil(total / FETCH_PAGE_SIZE);

  for (let page = 2; page <= pages; page += 1) {
    if (isCancelled()) return collected;
    const data = await getAgreements({
      ...params,
      page,
      page_size: FETCH_PAGE_SIZE,
    });
    collected.push(...asArray(data?.results));
  }

  return collected;
};

const getVisiblePages = (current, total, siblingCount = 1) => {
  if (total <= 0) return [];
  const maxVisible = siblingCount * 2 + 5;
  if (total <= maxVisible) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  const leftSibling = Math.max(current - siblingCount, 1);
  const rightSibling = Math.min(current + siblingCount, total);
  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < total - 1;
  const pages = [];

  if (!showLeftEllipsis && showRightEllipsis) {
    const leftCount = 3 + 2 * siblingCount;
    for (let i = 1; i <= leftCount; i += 1) pages.push(i);
    pages.push("ellipsis-right");
    pages.push(total);
  } else if (showLeftEllipsis && !showRightEllipsis) {
    pages.push(1);
    pages.push("ellipsis-left");
    const rightCount = 3 + 2 * siblingCount;
    for (let i = total - rightCount + 1; i <= total; i += 1) pages.push(i);
  } else {
    pages.push(1);
    pages.push("ellipsis-left");
    for (let i = leftSibling; i <= rightSibling; i += 1) pages.push(i);
    pages.push("ellipsis-right");
    pages.push(total);
  }

  return pages;
};

const filterPerformerOption = (option, rawInput) => {
  if (option?.data?.source === "user") {
    const query = String(rawInput || "").trim().toLowerCase();
    if (!query) return true;
    return String(option.label || option.data?.name || "")
      .toLowerCase()
      .includes(query);
  }
  return filterCounterpartySelectOption(option, rawInput);
};

const filterSelectStyles = {
  ...selectStyles,
  control: (provided, state) => ({
    ...provided,
    minHeight: 40,
    height: 40,
    borderRadius: 10,
    border: state.isFocused ? "1px solid #2a5e38" : "1px solid #e0e0e0",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(42, 94, 56, 0.12)" : "none",
    background: "#fff",
    padding: 0,
    fontFamily: "Manrope, sans-serif",
    fontSize: 14,
    cursor: "pointer",
    "&:hover": {
      borderColor: "#2a5e38",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: 38,
    padding: "0 12px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: 38,
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "0 8px",
    color: "#6b7280",
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: "0 2px",
  }),
  placeholder: (provided) => ({
    ...selectStyles.placeholder(provided),
    fontWeight: 400,
  }),
  singleValue: (provided) => ({
    ...selectStyles.singleValue(provided),
    fontWeight: 400,
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
    fontFamily: "Manrope, sans-serif",
    fontSize: 14,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 10000,
  }),
};

const StackList = ({ rows, emptyLabel = "—" }) => {
  if (!rows.length) return <span className="agr-muted">{emptyLabel}</span>;
  const visible = rows.slice(0, 4);
  const hidden = rows.length - visible.length;
  return (
    <ul className="agr-stack">
      {visible.map((row) => (
        <li key={row.id}>
          <span className="agr-stack__name">{row.name}</span>
          {row.price != null && (
            <span className="agr-stack__price">{formatCurrency(row.price)}</span>
          )}
          {row.complete ? <span className="agr-stack__done">закрыт</span> : null}
        </li>
      ))}
      {hidden > 0 && <li className="agr-muted">ещё {hidden}</li>}
    </ul>
  );
};

const StepsTable = ({ steps }) => {
  const sorted = sortSteps(steps);
  if (sorted.length === 0) {
    return <p className="agr-muted">Шаги не назначены</p>;
  }
  return (
    <table className="agr-mini">
      <thead>
        <tr>
          <th>№</th>
          <th>Согласующий</th>
          <th>Роль</th>
          <th>Статус</th>
          <th>Комментарий</th>
          <th>Решение</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((step) => (
          <tr key={step.id ?? step.step_order}>
            <td>{step.step_order ?? "—"}</td>
            <td>{stepLabel(step)}</td>
            <td>{stepRole(step) || "—"}</td>
            <td>
              <StatusBadge
                status={step.status}
                labels={APPROVAL_STEP_STATUS_LABELS}
              />
            </td>
            <td>{step.comment?.trim() || "—"}</td>
            <td>{formatDateTime(step.decided_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const AgreementExtra = ({ agreement, sectionNameById, blockNameById }) => {
  const [approval, setApproval] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getCurrentAgreementApproval(agreement.id).catch(() => null),
      getAgreementApprovals(agreement.id).catch(() => []),
    ]).then(([current, past]) => {
      if (cancelled) return;
      setApproval(current);
      setHistory(Array.isArray(past) ? past : []);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [agreement.id]);

  const steps =
    sortSteps(approval?.steps).length > 0
      ? sortSteps(approval?.steps)
      : sortSteps(agreement?.approval_summary?.steps);

  const currentApprovalId =
    approval?.id ?? agreement?.approval_summary?.approval_id ?? agreement?.approval_id;
  const pastApprovals = history.filter(
    (item) => item && item.id !== currentApprovalId && Array.isArray(item.steps),
  );

  const sections = getSectionRows(agreement, sectionNameById);
  const blocks = getBlockRows(agreement, blockNameById);
  const payments = Array.isArray(agreement?.payments) ? agreement.payments : [];
  const stages = Array.isArray(agreement?.workflow_stages)
    ? agreement.workflow_stages
    : [];
  const sendSummary = agreement?.subcontractor_send_summary;
  const attachments = Array.isArray(agreement?.attachments)
    ? agreement.attachments
    : [];

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await exportAgreementXlsx(agreement.id, { number: agreement.number });
    } catch (err) {
      setDownloadError(err?.message || "Не удалось скачать соглашение");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="agr-extra">
      <section className="agr-card">
        <h3>Согласование</h3>
        {agreement?.approval_summary?.current_approver_name && (
          <p className="agr-extra__current">
            Текущий шаг: {agreement.approval_summary.current_approver_name}
            {agreement.approval_summary.current_step_role
              ? ` · ${agreement.approval_summary.current_step_role}`
              : ""}
            {agreement.approval_summary.status
              ? ` · ${
                  APPROVAL_STATUS_LABELS[agreement.approval_summary.status] ??
                  agreement.approval_summary.status
                }`
              : ""}
          </p>
        )}
        {loading && steps.length === 0 ? (
          <p className="agr-muted">Загрузка шагов...</p>
        ) : (
          <StepsTable steps={steps} />
        )}
        {pastApprovals.length > 0 && (
          <div className="agr-history">
            {pastApprovals.map((item) => (
              <details key={item.id}>
                <summary>
                  <StatusBadge status={item.status} labels={APPROVAL_STATUS_LABELS} />
                  <span>{formatDateTime(item.created_at)}</span>
                  {item.initiator_name ? <span>Инициатор: {item.initiator_name}</span> : null}
                </summary>
                <StepsTable steps={item.steps} />
              </details>
            ))}
          </div>
        )}
      </section>

      <section className="agr-card">
        <h3>Платежи</h3>
        {payments.length === 0 ? (
          <p className="agr-muted">Платежей нет</p>
        ) : (
          <table className="agr-mini">
            <thead>
              <tr>
                <th>Сумма</th>
                <th>План</th>
                <th>Оплачен</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.id}>
                  <td>{formatCurrency(payment.amount)}</td>
                  <td>{formatDateRu(payment.planned_date) || "—"}</td>
                  <td>{formatDateTime(payment.paid_at)}</td>
                  <td>
                    <StatusBadge
                      status={payment.status}
                      labels={PAYMENT_STATUS_LABELS}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="agr-card">
        <h3>Разделы</h3>
        {sections.length === 0 ? (
          <p className="agr-muted">Разделы не указаны</p>
        ) : (
          <ul className="agr-detail-list">
            {sections.map((section) => (
              <li key={section.id}>
                <span>{section.name}</span>
                <strong>
                  {section.price != null ? formatCurrency(section.price) : "—"}
                </strong>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="agr-card">
        <h3>Блоки</h3>
        {blocks.length === 0 ? (
          <p className="agr-muted">Блоки не указаны</p>
        ) : (
          <ul className="agr-detail-list">
            {blocks.map((block) => (
              <li key={block.id}>
                <span>
                  {block.name}
                  {block.complete ? (
                    <span className="agr-stack__done">закрыт</span>
                  ) : null}
                </span>
                <strong>
                  {block.price != null ? formatCurrency(block.price) : "—"}
                </strong>
              </li>
            ))}
          </ul>
        )}
      </section>

      {stages.length > 0 && (
        <section className="agr-card agr-card--wide">
          <h3>Этапы</h3>
          <ul className="agr-detail-list">
            {stages.map((stage, index) => (
              <li key={`${stage.stage_name}-${index}`}>
                <span>
                  {stage.stage_name || "—"}
                  {(stage.executor_name || stage.role_type) && (
                    <span className="agr-steps__role">
                      {[stage.executor_name, stage.role_type].filter(Boolean).join(" · ")}
                    </span>
                  )}
                </span>
                <span className="agr-detail-list__aside">
                  <StatusBadge
                    status={stage.status}
                    labels={WORKFLOW_STATUS_LABELS}
                  />
                  {stage.decided_at ? (
                    <span className="agr-muted">{formatDateTime(stage.decided_at)}</span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="agr-card agr-card--wide">
        <h3>Дополнительно</h3>
        <div className="agr-meta">
          <div>
            <span>Тип исполнителя</span>
            <strong>
              {PERFORMER_TYPE_LABELS[agreement.performer_type] ||
                agreement.performer_type ||
                "—"}
            </strong>
          </div>
          <div>
            <span>Компания</span>
            <strong>{agreement.company_name || "—"}</strong>
          </div>
          <div>
            <span>Версия сметы</span>
            <strong>{agreement.smeta_version ?? "—"}</strong>
          </div>
          <div>
            <span>Срок</span>
            <strong>
              {[formatDateRu(agreement.date_from), formatDateRu(agreement.date_to)]
                .filter(Boolean)
                .join(" — ") || "—"}
            </strong>
          </div>
          {sendSummary && (
            <div>
              <span>Отправка субподрядчику</span>
              <strong>
                {sendSummary.counterparty_name || "—"}
                {sendSummary.status ? ` · ${sendSummary.status}` : ""}
                {sendSummary.sent_at
                  ? ` · отправлено ${formatDateTime(sendSummary.sent_at)}`
                  : ""}
                {sendSummary.signed_at
                  ? ` · подписано ${formatDateTime(sendSummary.signed_at)}`
                  : ""}
              </strong>
            </div>
          )}
        </div>
        {attachments.length > 0 && (
          <ul className="agr-files">
            {attachments.map((file) => (
              <li key={file.id}>
                {file.file_url ? (
                  <a href={file.file_url} target="_blank" rel="noreferrer">
                    {file.name || "Файл"}
                  </a>
                ) : (
                  file.name || "Файл"
                )}
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          className="agr-download"
          onClick={handleDownload}
          disabled={downloading}
        >
          {downloading ? "Скачивание..." : "Скачать соглашение"}
        </button>
        {downloadError && <p className="agr-error">{downloadError}</p>}
      </section>
    </div>
  );
};

const INITIAL_QUERY = {
  number: "",
  status: "",
  performerType: "",
  projectId: null,
  projectKind: "",
  performer: null,
  dateFrom: "",
  dateTo: "",
  ordering: "",
  page: 1,
  pageSize: 20,
};

const AgreementsRegistry = () => {
  const { userData } = useContext(UserContext);
  const hasAccess = userHasAgreementsRegistryAccess(userData);
  const canOpenFinance = userHasFinanceLink(userData);

  const [numberInput, setNumberInput] = useState("");
  const [query, setQuery] = useState(INITIAL_QUERY);
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectNameById, setProjectNameById] = useState({});
  const [projectKindById, setProjectKindById] = useState({});
  const [performerGroups, setPerformerGroups] = useState([]);
  const [counterpartyNameById, setCounterpartyNameById] = useState({});
  const [performerNameById, setPerformerNameById] = useState({});
  const [sectionNameById, setSectionNameById] = useState({});
  const [blockNameById, setBlockNameById] = useState({});
  const [directoriesError, setDirectoriesError] = useState(null);

  const [agreements, setAgreements] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [clientRows, setClientRows] = useState({ key: null, rows: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = numberInput.trim();
      setQuery((prev) => {
        if (prev.number === next) return prev;
        return { ...prev, number: next, page: 1 };
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [numberInput]);

  useEffect(() => {
    if (!hasAccess) return undefined;
    let cancelled = false;

    const loadDirectories = async () => {
      const [projectsResult, counterpartiesResult, usersResult, sectionsResult, blocksResult] =
        await Promise.allSettled([
          getAllProjectsSummary(),
          Promise.all([
            getAllCounterparties({ counterparty_side: "counterparty" }),
            getAllCounterparties({ counterparty_side: "our_company" }),
          ]),
          getUsersDirectory(),
          getAllSections(),
          getAllBlocks(),
        ]);

      if (cancelled) return;

      if (projectsResult.status === "fulfilled") {
        const names = {};
        const kinds = {};
        const options = asArray(projectsResult.value)
          .filter((project) => project?.id != null)
          .map((project) => {
            const code = project.code || project.cipher_pilot || "";
            const name = project.name || "Без названия";
            const id = Number(project.id);
            names[id] = name;
            const catalogKind = String(project.building_type || "")
              .trim()
              .toUpperCase();
            kinds[id] =
              catalogKind === "МЖК" || catalogKind === "ОЗ"
                ? catalogKind
                : projectKindOfName(name);
            return {
              value: project.id,
              label: code ? `${code} — ${name}` : name,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label, "ru"));
        setProjectNameById(names);
        setProjectKindById(kinds);
        setProjectOptions(options);
      }

      const counterpartyList =
        counterpartiesResult.status === "fulfilled"
          ? asArray(counterpartiesResult.value?.[0])
          : [];
      const ourCompanyList =
        counterpartiesResult.status === "fulfilled"
          ? asArray(counterpartiesResult.value?.[1])
          : [];
      const counterpartyNames = {};
      const rememberCounterparty = (item) => {
        if (item?.id == null) return;
        counterpartyNames[Number(item.id)] = getCounterpartyLabel(item);
      };
      counterpartyList.forEach(rememberCounterparty);
      ourCompanyList.forEach(rememberCounterparty);
      const counterparties = counterpartyList
        .filter((item) => item?.id != null)
        .map((item) => ({
          value: `counterparty:${item.id}`,
          id: item.id,
          source: "counterparty",
          name: getCounterpartyLabel(item),
          label: getCounterpartySelectLabel(item),
          data: item,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, "ru"));
      setCounterpartyNameById(counterpartyNames);

      const performerNames = {};
      const users =
        usersResult.status === "fulfilled"
          ? asArray(usersResult.value)
              .filter((user) => user?.id != null)
              .map((user) => {
                const name = formatUserDisplayName(user);
                performerNames[Number(user.id)] = name;
                return {
                  value: `user:${user.id}`,
                  id: user.id,
                  source: "user",
                  name,
                  label: name,
                };
              })
              .sort((a, b) => a.label.localeCompare(b.label, "ru"))
          : [];
      setPerformerNameById(performerNames);

      setPerformerGroups(
        [
          counterparties.length > 0 && {
            label: "Контрагенты",
            options: counterparties,
          },
          users.length > 0 && { label: "Сотрудники", options: users },
        ].filter(Boolean),
      );

      if (sectionsResult.status === "fulfilled") {
        const map = {};
        asArray(sectionsResult.value).forEach((section) => {
          const id = Number(section?.id ?? section?.section_id);
          const name = section?.name || section?.section?.name;
          if (Number.isFinite(id) && name) map[id] = name;
        });
        setSectionNameById(map);
      }

      if (blocksResult.status === "fulfilled") {
        const map = {};
        asArray(blocksResult.value).forEach((block) => {
          const id = Number(block?.id ?? block?.block_id);
          const name = block?.name || block?.block?.name;
          if (Number.isFinite(id) && name) map[id] = name;
        });
        setBlockNameById(map);
      }

      const failed = [
        projectsResult,
        counterpartiesResult,
        usersResult,
        blocksResult,
      ].filter((result) => result.status === "rejected");
      setDirectoriesError(
        failed.length > 0
          ? "Часть справочников не загрузилась. Названия и фильтры могут быть неполными."
          : null,
      );
    };

    loadDirectories();
    return () => {
      cancelled = true;
    };
  }, [hasAccess]);

  const serverParams = useMemo(
    () => ({
      number: query.number || undefined,
      date_from: query.dateFrom || undefined,
      date_to: query.dateTo || undefined,
      performer_type: query.performerType || undefined,
      project_id: query.projectId ?? undefined,
      ordering: query.ordering || undefined,
    }),
    [
      query.number,
      query.dateFrom,
      query.dateTo,
      query.performerType,
      query.projectId,
      query.ordering,
    ],
  );
  const needsLocalList = Boolean(
    query.performer || query.projectKind || isApprovalListStatus(query.status),
  );
  const localKey = needsLocalList
    ? JSON.stringify({
        ...serverParams,
        performer: query.performer?.value ?? null,
        projectKind: query.projectKind || "",
        status: isApprovalListStatus(query.status) ? "on_approval" : query.status || "",
        names: Object.keys(projectNameById).length,
        kinds: Object.keys(projectKindById).length,
      })
    : null;

  useEffect(() => {
    if (!hasAccess || needsLocalList) return undefined;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getAgreements({
          ...serverParams,
          status: query.status || undefined,
          page: query.page,
          page_size: query.pageSize,
        });
        if (cancelled) return;
        setAgreements(asArray(data?.results));
        setTotalCount(Number(data?.count) || 0);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || "Не удалось загрузить соглашения");
        setAgreements([]);
        setTotalCount(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    hasAccess,
    needsLocalList,
    query.page,
    query.pageSize,
    query.status,
    serverParams,
  ]);

  useEffect(() => {
    if (!hasAccess || !needsLocalList) return undefined;
    let cancelled = false;
    const key = localKey;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const all = await fetchAllAgreements(
          {
            ...serverParams,
            status: isApprovalListStatus(query.status)
              ? undefined
              : query.status || undefined,
          },
          () => cancelled,
        );
        if (cancelled) return;
        const filtered = sortAgreements(
          all.filter((item) => {
            if (query.performer && !matchesPerformer(item, query.performer)) {
              return false;
            }
            if (
              query.projectKind &&
              resolveProjectKind(item, projectNameById, projectKindById) !==
                query.projectKind
            ) {
              return false;
            }
            if (
              isApprovalListStatus(query.status) &&
              !matchesApprovalListStatus(item)
            ) {
              return false;
            }
            return true;
          }),
          query.ordering,
        );
        setClientRows({ key, rows: filtered });
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || "Не удалось загрузить соглашения");
        setClientRows({ key, rows: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    hasAccess,
    needsLocalList,
    localKey,
    query.performer,
    query.projectKind,
    query.status,
    query.ordering,
    serverParams,
    projectNameById,
    projectKindById,
  ]);

  const projectOption = useMemo(
    () => projectOptions.find((option) => option.value === query.projectId) || null,
    [projectOptions, query.projectId],
  );

  const localReady = needsLocalList && clientRows.key === localKey;
  const visibleAgreements = needsLocalList
    ? localReady
      ? clientRows.rows.slice(
          (query.page - 1) * query.pageSize,
          query.page * query.pageSize,
        )
      : []
    : agreements;
  const visibleTotal = needsLocalList
    ? localReady
      ? clientRows.rows.length
      : 0
    : totalCount;

  const totalPages = Math.max(1, Math.ceil(visibleTotal / query.pageSize) || 1);
  const visiblePages = useMemo(
    () => getVisiblePages(Math.min(query.page, totalPages), totalPages),
    [query.page, totalPages],
  );

  useEffect(() => {
    if (query.page > totalPages) {
      setQuery((prev) =>
        prev.page > totalPages ? { ...prev, page: totalPages } : prev,
      );
    }
  }, [query.page, totalPages]);

  const patchQuery = (partial) => {
    setQuery((prev) => ({ ...prev, ...partial, page: 1 }));
  };

  const toggleOrdering = (field) => {
    setQuery((prev) => {
      let ordering = field;
      if (prev.ordering === field) ordering = `-${field}`;
      else if (prev.ordering === `-${field}`) ordering = "";
      return { ...prev, ordering, page: 1 };
    });
  };

  const resetFilters = () => {
    setNumberInput("");
    setQuery((prev) => ({
      ...INITIAL_QUERY,
      pageSize: prev.pageSize,
    }));
  };

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const pageSum = visibleAgreements.reduce(
    (sum, agreement) => sum + (Number(agreement?.price) || 0),
    0,
  );
  const rangeStart = visibleTotal === 0 ? 0 : (query.page - 1) * query.pageSize + 1;
  const rangeEnd = Math.min(query.page * query.pageSize, visibleTotal);
  const tableLoading = loading || (needsLocalList && !localReady);

  const renderSortHeader = (field, label) => {
    const active = query.ordering === field || query.ordering === `-${field}`;
    const mark = query.ordering === `-${field}` ? "↓" : query.ordering === field ? "↑" : "";
    return (
      <th>
        <button
          type="button"
          className={`agr-sort${active ? " agr-sort--active" : ""}`}
          onClick={() => toggleOrdering(field)}
        >
          {label}
          <span aria-hidden="true">{mark}</span>
        </button>
      </th>
    );
  };

  if (!hasAccess) {
    return (
      <div className="agr-page">
        <div className="agr-denied">У вас нет доступа к этой странице</div>
      </div>
    );
  }

  return (
    <div className="agr-page">
      <div className="agr-header">
        <h1>Соглашения</h1>
        <p>
          {tableLoading
            ? "Загрузка..."
            : `Найдено ${visibleTotal}. Сумма на странице ${formatCurrency(pageSum)}`}
        </p>
      </div>

      <div className="agr-filters">
        <label className="agr-field">
          <span>Номер</span>
          <input
            type="search"
            value={numberInput}
            placeholder="Номер соглашения"
            onChange={(event) => setNumberInput(event.target.value)}
          />
        </label>
        <label className="agr-field">
          <span>Статус</span>
          <select
            value={query.status}
            onChange={(event) => patchQuery({ status: event.target.value })}
          >
            <option value="">Все статусы</option>
            {STATUS_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="agr-field">
          <span>Проект</span>
          <Select
            inputId="agreements-project-filter"
            styles={filterSelectStyles}
            options={projectOptions}
            value={projectOption}
            onChange={(option) => patchQuery({ projectId: option?.value ?? null })}
            placeholder="Все проекты"
            isClearable
            isSearchable
            menuPortalTarget={document.body}
            menuPosition="fixed"
            noOptionsMessage={() => "Проекты не найдены"}
          />
        </label>
        <label className="agr-field">
          <span>Тип проекта</span>
          <select
            value={query.projectKind}
            onChange={(event) => patchQuery({ projectKind: event.target.value })}
          >
            <option value="">Все</option>
            <option value="МЖК">МЖК</option>
            <option value="ОЗ">ОЗ</option>
          </select>
        </label>
        <label className="agr-field">
          <span>Исполнитель</span>
          <Select
            inputId="agreements-performer-filter"
            styles={filterSelectStyles}
            options={performerGroups}
            value={query.performer}
            onChange={(option) => patchQuery({ performer: option || null })}
            placeholder="Все исполнители"
            isClearable
            isSearchable
            menuPortalTarget={document.body}
            menuPosition="fixed"
            filterOption={filterPerformerOption}
            noOptionsMessage={() => "Исполнители не найдены"}
          />
        </label>
        <label className="agr-field">
          <span>Тип исполнителя</span>
          <select
            value={query.performerType}
            onChange={(event) => patchQuery({ performerType: event.target.value })}
          >
            <option value="">Все типы</option>
            {Object.entries(PERFORMER_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="agr-field">
          <span>Дата с</span>
          <input
            type="date"
            value={query.dateFrom}
            onChange={(event) => patchQuery({ dateFrom: event.target.value })}
          />
        </label>
        <label className="agr-field">
          <span>Дата по</span>
          <input
            type="date"
            value={query.dateTo}
            onChange={(event) => patchQuery({ dateTo: event.target.value })}
          />
        </label>
        <button type="button" className="agr-reset" onClick={resetFilters}>
          Сбросить
        </button>
      </div>

      {directoriesError && <div className="agr-note">{directoriesError}</div>}
      {error && (
        <div className="agr-error" role="alert">
          {error}
        </div>
      )}

      <div className={`agr-table-wrap${tableLoading ? " agr-table-wrap--loading" : ""}`}>
        <table className="agr-table">
          <thead>
            <tr>
              {renderSortHeader("number", "Номер")}
              <th>Проект</th>
              <th>Исполнитель</th>
              <th>Разделы</th>
              <th>Блоки</th>
              {renderSortHeader("price", "Сумма")}
              {renderSortHeader("date_from", "Срок")}
              {renderSortHeader("status", "Статус")}
            </tr>
          </thead>
          <tbody>
            {tableLoading && visibleAgreements.length === 0 && (
              <tr>
                <td colSpan={8} className="agr-empty">
                  Загрузка соглашений...
                </td>
              </tr>
            )}
            {!tableLoading && visibleAgreements.length === 0 && (
              <tr>
                <td colSpan={8} className="agr-empty">
                  Соглашения не найдены
                </td>
              </tr>
            )}
            {visibleAgreements.map((agreement) => {
              const expanded = expandedIds.has(agreement.id);
              const performerName = getCounterpartyDisplayName(
                agreement,
                counterpartyNameById,
                performerNameById,
              );
              const performerNames = Array.isArray(agreement.performer_names)
                ? agreement.performer_names.filter(Boolean)
                : [];
              const sections = getSectionRows(agreement, sectionNameById);
              const blocks = getBlockRows(agreement, blockNameById);
              const period =
                [formatDateRu(agreement.date_from), formatDateRu(agreement.date_to)]
                  .filter(Boolean)
                  .join(" — ") || "—";

              return (
                <React.Fragment key={agreement.id}>
                  <tr
                    className={expanded ? "agr-row agr-row--open" : "agr-row"}
                    onClick={() => toggleExpanded(agreement.id)}
                  >
                    <td className="agr-number">
                      <span className="agr-chevron" aria-hidden="true">
                        {expanded ? "▼" : "▶"}
                      </span>
                      {formatAgreementNumber(agreement.number)}
                    </td>
                    <td>
                      <div className="agr-project">
                        <span>{resolveProjectName(agreement, projectNameById)}</span>
                        {canOpenFinance && agreement.project_id != null && (
                          <Link
                            to={`/projects/finance/${agreement.project_id}`}
                            onClick={(event) => event.stopPropagation()}
                          >
                            Финансы
                          </Link>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="agr-person">
                        <span>{performerName}</span>
                        <span className="agr-person__type">
                          {PERFORMER_TYPE_LABELS[agreement.performer_type] ||
                            agreement.performer_type ||
                            ""}
                        </span>
                        {performerNames.length > 0 &&
                          performerNames.join(", ") !== performerName && (
                            <span className="agr-person__type">
                              {performerNames.join(", ")}
                            </span>
                          )}
                      </div>
                    </td>
                    <td>
                      <StackList rows={sections} emptyLabel="—" />
                    </td>
                    <td>
                      <StackList rows={blocks} emptyLabel="—" />
                    </td>
                    <td className="agr-price">{formatCurrency(agreement.price)}</td>
                    <td>{period}</td>
                    <td>
                      <StatusBadge
                        status={agreement.status}
                        labels={AGREEMENT_STATUS_LABELS}
                      />
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="agr-extra-row">
                      <td colSpan={8}>
                        <AgreementExtra
                          agreement={agreement}
                          sectionNameById={sectionNameById}
                          blockNameById={blockNameById}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="agr-footer">
        <div className="agr-pager">
          <button
            type="button"
            onClick={() =>
              setQuery((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))
            }
            disabled={query.page <= 1 || tableLoading}
          >
            ‹
          </button>
          {visiblePages.map((page) =>
            typeof page === "string" ? (
              <span key={page} className="agr-pager__gap">
                …
              </span>
            ) : (
              <button
                key={page}
                type="button"
                className={page === query.page ? "is-active" : ""}
                onClick={() => setQuery((prev) => ({ ...prev, page }))}
                disabled={tableLoading}
              >
                {page}
              </button>
            ),
          )}
          <button
            type="button"
            onClick={() =>
              setQuery((prev) => ({
                ...prev,
                page: Math.min(totalPages, prev.page + 1),
              }))
            }
            disabled={query.page >= totalPages || tableLoading}
          >
            ›
          </button>
        </div>
        <label className="agr-page-size">
          На странице
          <select
            value={query.pageSize}
            onChange={(event) =>
              patchQuery({ pageSize: Number(event.target.value) })
            }
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
        <span className="agr-range">
          {rangeStart}–{rangeEnd} из {visibleTotal}
        </span>
      </div>
    </div>
  );
};

export default AgreementsRegistry;
